import type { U2000Condition } from "../../components/tables/basic/U2000BasicSearch";
import type { U2000TableColumn } from "../../components/tables/basic/U2000BasicTableList";

export const U2000SearchStateEnumAction = {
  SETCOLUMNS: "SETCOLUMNS",
  ADDORUPDATECRITERIAS: "ADDORUPDATECRITERIAS",
  DELETECRITERIAS: "DELETECRITERIAS",
  SETCRITERIAS: "SETCRITERIAS",
  SETFIELDS: "SETFIELDS",
  SETSORTEDFIELDS: "SETSORTEDFIELDS",
  UPDATESAVEDFILTERS: "UPDATESAVEDFILTERS",
  SETCURRENTFILTER: "SETCURRENTFILTER",
  RESTOREFILTER: "RESTOREFILTER",
} as const;

export interface U2000SearchParameterCriteria {
  field: string;
  operator: string;
  value: any;
  type: string;
}

export interface U2000SearchParameterField {
  name: string;
}

export interface U2000SearchParameterSortedField {
  name: string;
  sortOrder?: string;
}

export interface U2000SearchParameter {
  criterias: U2000SearchParameterCriteria[];
  fields: U2000SearchParameterField[];
  sortedFields: U2000SearchParameterSortedField[];
}

export interface Field {
  name: string;
  label?: string;
}

export interface SortedField {
  name: string;
  label?: string;
  sortOrder?: string;
}

export interface CriteriaDynamicDate {
  single?: string;
  intervalStart?: string;
  intervalEnd?: string;
}

export interface Criteria {
  id: string;
  field: U2000TableColumn;
  condition: U2000Condition;
  value: string;
  /** Libellé métier pour affichage et réédition (BL-0039 criteriaLookup) */
  displayValue?: string;
  /** BL-0452 — token(s) date dynamique persisté(s) en localStorage */
  dynamicDate?: CriteriaDynamicDate;
  timestamps: number;
}

export interface U2000SearchStateFilter {
  fields: Field[];
  criterias: Criteria[];
  sortedFields: SortedField[];
}

export interface U2000SearchStateSavedCriteria {
  id: string;
  name: string;
  isDefaultFilter: boolean;
  filter: U2000SearchStateFilter;
}

export interface U2000SearchState {
  columns: U2000TableColumn[];
  currentFilter: U2000SearchStateFilter;
  oldFilter?: U2000SearchStateFilter;
  savedFilters: U2000SearchStateSavedCriteria[];
}

export interface U2000SearchAction {
  type: string;
  [key: string]: any;
}

function handleSetColumns(searchState: U2000SearchState, action: U2000SearchAction): U2000SearchState {
  const { columns } = action.data;

  return {
    ...searchState,
    columns: columns,
  };
}

// Criterias add or update

function handleSetCriterias(searchState: U2000SearchState, action: U2000SearchAction): U2000SearchState {
  const { criterias } = action.data;

  return {
    ...searchState,
    currentFilter: {
      ...searchState.currentFilter,
      criterias,
    },
  };
}

function handleAddOrUpdateCriterias(searchState: U2000SearchState, action: U2000SearchAction): U2000SearchState {
  const { field, condition, value, displayValue } = action.data;
  const existingCriteriaIndex = searchState.currentFilter.criterias.findIndex((criteria) => criteria.field.name === field.name);
  const now = Date.now();

  let updatedCriterias = [...searchState.currentFilter.criterias];

  if (existingCriteriaIndex !== -1) {
    updatedCriterias = updatedCriterias.map((criteria, index) => {
      if (index !== existingCriteriaIndex) {
        return criteria;
      }
      const { dynamicDate: _dynamicDate, ...rest } = criteria;
      return { ...rest, condition, value, displayValue, timestamps: now };
    });
  } else {
    updatedCriterias.push({
      id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2),
      field: field,
      condition,
      value,
      displayValue,
      timestamps: now,
    });
  }

  return {
    ...searchState,
    currentFilter: {
      ...searchState.currentFilter,
      criterias: updatedCriterias,
    },
  };
}

function handleDeleteCriterias(searchState: U2000SearchState, action: U2000SearchAction): U2000SearchState {
  const { name } = action.data;

  return {
    ...searchState,
    currentFilter: {
      ...searchState.currentFilter,
      criterias: searchState.currentFilter.criterias.filter((criteria) => criteria.field.name !== name),
    },
  };
}

function handleSetFields(searchState: U2000SearchState, action: U2000SearchAction): U2000SearchState {
  const { fields } = action.data;

  return {
    ...searchState,
    currentFilter: {
      ...searchState.currentFilter,
      fields,
    },
  };
}

function handleSetSortedFields(searchState: U2000SearchState, action: U2000SearchAction): U2000SearchState {
  const { sortedFields } = action.data;

  return {
    ...searchState,
    currentFilter: {
      ...searchState.currentFilter,
      sortedFields,
    },
  };
}

function handleUpdateSavedFilters(searchState: U2000SearchState, action: U2000SearchAction): U2000SearchState {
  const { savedFilters } = action.data;

  return {
    ...searchState,
    savedFilters: savedFilters,
  };
}

function handleSetCurrentFilter(searchState: U2000SearchState, action: U2000SearchAction): U2000SearchState {
  const { filter } = action.data;

  return {
    ...searchState,
    oldFilter: searchState.currentFilter,
    currentFilter: filter,
  };
}

function handleRestoreFilter(searchState: U2000SearchState, _action: U2000SearchAction): U2000SearchState {
  return {
    ...searchState,
    currentFilter: searchState.oldFilter || searchState.currentFilter,
  };
}

// Sorted fields add or update

const actionHandlers: Record<string, (searchState: U2000SearchState, action: U2000SearchAction) => U2000SearchState> = {
  [U2000SearchStateEnumAction.SETCOLUMNS]: handleSetColumns,
  [U2000SearchStateEnumAction.ADDORUPDATECRITERIAS]: handleAddOrUpdateCriterias,
  [U2000SearchStateEnumAction.DELETECRITERIAS]: handleDeleteCriterias,
  [U2000SearchStateEnumAction.SETCRITERIAS]: handleSetCriterias,
  [U2000SearchStateEnumAction.SETFIELDS]: handleSetFields,
  [U2000SearchStateEnumAction.SETSORTEDFIELDS]: handleSetSortedFields,
  [U2000SearchStateEnumAction.UPDATESAVEDFILTERS]: handleUpdateSavedFilters,
  [U2000SearchStateEnumAction.SETCURRENTFILTER]: handleSetCurrentFilter,
  [U2000SearchStateEnumAction.RESTOREFILTER]: handleRestoreFilter,
};

export default function u2000SearchState(searchState: U2000SearchState, action: U2000SearchAction): U2000SearchState {
  const handler = actionHandlers[action.type];
  if (!handler) {
    throw new Error(`Unhandled action type: ${action.type}`);
  }
  return handler(searchState, action);
}
